import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { capitalizeString, formatCoordinate } from 'common/utils';
import {
  Card,
  BannerComponent,
  IconList,
  LevelNav,
  BackButton,
  Dropdown,
  Online,
} from 'common/components';
import { ACTIVITY_TYPES, OTHER_ACTIVITY_TYPES } from 'common/constants';
import * as icons from 'common/svg/icons';
import { toCommaNumber } from 'common/formatters';
import { SocialShare } from 'containers';

const ActivityDetails = ({
  field,
  activity,
  season,
  selectedCrop,
  deleteAction,
  goToEdit,
}) => {
  const _getActivityStatus = (activityDate, finishDate) => {
    if (finishDate) {
      return { text: 'Done', backgroundColor: '#006332' };
    }

    if (moment(activityDate).isBefore(moment().startOf('day'))) {
      return { text: 'Overdue', backgroundColor: '#BB4300' };
    }
    return { text: 'Planned', backgroundColor: '#EBA008' };
  };

  const getFieldsLabel = obj =>
    `${toCommaNumber(obj.size)} ${capitalizeString(obj.type)}`;

  const renderDetails = activity => {
    const {
      crop,
      fieldActivityType,
      activityType,
      source,
      destinations,
      destination,
    } = activity;
    const location = !!activity && activity.location;
    const list = [];
    let cardTitle = '';

    // all activities have crop info
    crop &&
      list.push({
        icon: 'crop',
        label: crop && `${crop.name} ${crop.variety ? crop.variety : ''}`,
      });

    if (fieldActivityType) {
      cardTitle = field.name;
      field &&
        list.push({
          icon: 'fields',
          label:
            (selectedCrop && getFieldsLabel(selectedCrop.plantedArea)) ||
            (field && getFieldsLabel(field.size)),
        });
      location &&
        list.push({
          icon: 'coordinates',
          label: `${formatCoordinate(location.lat)}, ${formatCoordinate(
            location.lng
          )}`,
        });
      location &&
        list.push({
          icon: 'target',
          label: (
            <a
              href={`https://www.google.com/maps/dir//${location.lat},${
                location.lng
              }`}
              target="_blank"
            >
              Get Direction
            </a>
          ),
        });
    } else if (activityType === ACTIVITY_TYPES.STORAGE) {
      const totalAmount =
        destinations &&
        destinations.reduce(
          (current, destination) => destination.amount.size + current,
          0
        );
      cardTitle = source && source.name;
      source &&
        destinations &&
        list.push({
          icon: 'storage',
          label: `${toCommaNumber(totalAmount) || 0} ${capitalizeString(
            destinations[0].amount.type
          )}`,
        });
    } else if (activityType === ACTIVITY_TYPES.DELIVERY) {
      cardTitle = destination && destination.destination.name;
      destination &&
        destination.amount &&
        list.push({
          icon: 'storagefill',
          label: `${toCommaNumber(destination.amount.size)} ${capitalizeString(
            destination.amount.type
          )}`,
        });
    }

    const status = _getActivityStatus(
      activity.activityDate,
      activity.finishDate
    );

    return (
      <Card>
        <h3 className={'cardlist__title'}>{cardTitle}</h3>
        {status && (
          <p className="cardlist__status">
            {status.text}{' '}
            <span style={{ backgroundColor: status.backgroundColor }} />
          </p>
        )}
        <IconList list={list} />
      </Card>
    );
  };

  const editTool = {
    label: 'Edit',
    icon: 'edit',
    action: goToEdit,
  };
  const deleteTool = {
    label: 'Delete',
    icon: 'garbage',
    action: deleteAction,
  };

  const toolList = [];
  if (activity.fieldActivityType) {
    switch (activity.fieldActivityType) {
      case ACTIVITY_TYPES.SPRAYING:
      case ACTIVITY_TYPES.FERTILIZING:
      case ACTIVITY_TYPES.SCOUTING:
      case ACTIVITY_TYPES.SEEDING:
      case ACTIVITY_TYPES.STORAGE:
      case OTHER_ACTIVITY_TYPES.TILLING:
      case OTHER_ACTIVITY_TYPES.IRRIGATION:
      case OTHER_ACTIVITY_TYPES.CUSTOM:
        toolList.push(editTool, deleteTool);
        break;
      case ACTIVITY_TYPES.HARVESTING:
        // only show edit/delete buttons in the current/future season
        if (season >= new Date().getFullYear()) {
          toolList.push(editTool, deleteTool);
        }
        break;
    }
  } else if (activity.activityType) {
    switch (activity.activityType) {
      case ACTIVITY_TYPES.STORAGE:
      case ACTIVITY_TYPES.DELIVERY:
        // only show edit/delete buttons in the current/future season
        if (season >= new Date().getFullYear()) {
          toolList.push(editTool, deleteTool);
        }
        break;
    }
  }

  const tools = (
    <Fragment>
      <SocialShare id={activity.id} season={season} />

      {!!toolList.length && (
        <Online>
          <Dropdown
            className="level-right"
            theme={['right', 'medium', 'invert']}
            trigger={<icons.kebab />}
          >
            <IconList list={toolList} theme={['medium']} />
          </Dropdown>
        </Online>
      )}
    </Fragment>
  );

  return (
    <div className="bg--primary">
      <BannerComponent
        theme={['strong', 'static', 'mobile-fixed']}
        message={
          <LevelNav
            nav={{
              left: {
                element: <BackButton />,
              },
              center: {
                label: activity
                  ? `${activity.fieldActivityType ||
                      activity.activityType} ACTIVITY`
                  : '',
              },
              right: { element: tools },
            }}
          />
        }
      />
      <div className={'column__inner'}>
        <section className={'cardlist__item'}>
          {renderDetails(activity)}
        </section>
      </div>
    </div>
  );
};

ActivityDetails.propTypes = {
  title: PropTypes.string,
  field: PropTypes.object,
  crops: PropTypes.array,
  activity: PropTypes.object,
  season: PropTypes.number,
  selectedCrop: PropTypes.object,
  deleteAction: PropTypes.func,
  goToEdit: PropTypes.func,
};

export default connect(({ settings }) => ({ season: settings.season }))(
  ActivityDetails
);



// WEBPACK FOOTER //
// ./src/containers/ActivityDetails/index.js